"use client";

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Loader2 } from "lucide-react";

interface MisstatementFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (misstatementId: string) => void;
}

export const MisstatementFormModal: React.FC<MisstatementFormModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [workspace, setWorkspace] = useState<string>("");
  const [misstatementType, setMisstatementType] = useState<string>("");
  const [workspaceOptions, setWorkspaceOptions] = useState<string[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load workspaces from Sections.json
  useEffect(() => {
    const loadWorkspaces = async () => {
      if (!isOpen) return;
      setLoadingWorkspaces(true);
      try {
        if (
          (window as any).sharePointAPI &&
          typeof (window as any).sharePointAPI.downloadSectionsJson === "function"
        ) {
          const res = await (window as any).sharePointAPI.downloadSectionsJson();
          if (res.success && res.data) {
            const sectionsData = res.data;
            const workspaces = new Set<string>();

            // Extract workspaces from Planning, Execution, and ConcludingProcedures
            if (sectionsData.Planning) {
              Object.keys(sectionsData.Planning).forEach((ws) => workspaces.add(ws));
            }
            if (sectionsData.Execution) {
              Object.keys(sectionsData.Execution).forEach((ws) => workspaces.add(ws));
            }
            if (sectionsData.ConcludingProcedures) {
              Object.keys(sectionsData.ConcludingProcedures).forEach((ws) =>
                workspaces.add(ws)
              );
            }

            setWorkspaceOptions(Array.from(workspaces).sort());
          } else {
            setWorkspaceOptions([]);
          }
        } else {
          setWorkspaceOptions([]);
        }
      } catch (err) {
        console.error("Failed to load workspaces from Sections.json", err);
        setWorkspaceOptions([]);
      } finally {
        setLoadingWorkspaces(false);
      }
    };

    loadWorkspaces();
  }, [isOpen]);

  const resetForm = () => {
    setWorkspace("");
    setMisstatementType("");
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    if (!workspace || !misstatementType) {
      alert("Please select both workspace and type of misstatement");
      return;
    }

    try {
      setSaving(true);

      if (
        !(window as any).sharePointAPI ||
        typeof (window as any).sharePointAPI.createMisstatement !== "function"
      ) {
        throw new Error("SharePoint API for misstatement creation is not available");
      }

      const payload = {
        workspace: workspace,
        type: misstatementType,
      };

      const result = await (window as any).sharePointAPI.createMisstatement(payload);
      if (!result || !result.success || !result.data?.misstatementId) {
        throw new Error(result?.error || "Failed to create misstatement");
      }

      const misstatementId: string = result.data.misstatementId;

      if (onCreated) {
        onCreated(misstatementId);
      }

      resetForm();
      onClose();
    } catch (error) {
      console.error("Failed to create misstatement", error);
      alert(
        `Failed to create misstatement: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl bg-black/90 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Add Misstatement</DialogTitle>
          <DialogDescription className="text-white/60">
            Create a new misstatement entry in the Misstatement Library.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Workspace dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">
              Workspace <span className="text-red-400">*</span>
            </label>
            <Select value={workspace} onValueChange={(val) => setWorkspace(val)}>
              <SelectTrigger>
                <SelectValue placeholder="Select workspace" />
              </SelectTrigger>
              <SelectContent>
                {loadingWorkspaces ? (
                  <div className="flex items-center justify-center py-4 text-white/60 text-sm gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading workspaces...
                  </div>
                ) : workspaceOptions.length === 0 ? (
                  <div className="py-2 text-xs text-white/60 px-1">
                    No workspaces found. Ensure Sections.json exists in SharePoint.
                  </div>
                ) : (
                  workspaceOptions.map((ws) => (
                    <SelectItem key={ws} value={ws}>
                      {ws}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Type of Misstatement dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">
              Type of Misstatement <span className="text-red-400">*</span>
            </label>
            <Select
              value={misstatementType}
              onValueChange={(val) => setMisstatementType(val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type of misstatement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Factual">Factual</SelectItem>
                <SelectItem value="Judgemental">Judgemental</SelectItem>
                <SelectItem value="Projected">Projected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={saving}
            className="border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={saving || !workspace || !misstatementType}
            className="bg-blue-500 text-white hover:bg-blue-600"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </span>
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

